import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'

/**
 * How much room a card's header, content and footer leave inside its edge:
 * `default` is the system's 16px (`space-4`), the same as a table cell, for a
 * card among others on a page; `lg` is 24px, for a card that is the whole
 * page — sign-in, a confirmation, an error you landed on.
 */
type CardSize = 'default' | 'lg'

const CardSizeContext = React.createContext<CardSize>('default')

const PAD: Record<CardSize, { header: string; body: string }> = {
  default: { header: 'p-4', body: 'p-4 pt-0' },
  lg: { header: 'p-6', body: 'p-6 pt-0' },
}

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: CardSize
  /** Draw the card onto its single child (a `<section>`, an `<li>`, a `<form>`) instead of a `<div>`. */
  asChild?: boolean
}

/**
 * A surface container for grouping related content: white, on the hairline,
 * with the surface corner and no shadow. Use `CardHeader`/`CardContent` for a
 * titled card; a panel with no header sets its own padding (`p-4`, the default
 * size's).
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, size = 'default', asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'div'
  return (
    <CardSizeContext.Provider value={size}>
      <Comp ref={ref} className={cn('rounded-lg border border-line bg-surface text-ink', className)} {...props} />
    </CardSizeContext.Provider>
  )
})

/** The header region of a `Card`. */
export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function CardHeader(
  { className, ...props },
  ref,
) {
  const size = React.useContext(CardSizeContext)
  return <div ref={ref} className={cn('flex flex-col gap-1', PAD[size].header, className)} {...props} />
})

/** The title line of a `Card` header, in the heading style. */
export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(function CardTitle(
  { className, ...props },
  ref,
) {
  return <h3 ref={ref} className={cn('m-0 text-heading font-semibold text-ink', className)} {...props} />
})

/** Secondary descriptive text within a `Card` header. */
export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  function CardDescription({ className, ...props }, ref) {
    return <p ref={ref} className={cn('m-0 text-sm text-ink-muted', className)} {...props} />
  },
)

/** The main body region of a `Card`. */
export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function CardContent(
  { className, ...props },
  ref,
) {
  const size = React.useContext(CardSizeContext)
  return <div ref={ref} className={cn(PAD[size].body, className)} {...props} />
})

/**
 * The footer region of a `Card`, typically holding actions.
 *
 * @public Part of the card's documented anatomy (design-system/COMPONENTS.md),
 * though no screen uses it yet.
 */
export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function CardFooter(
  { className, ...props },
  ref,
) {
  const size = React.useContext(CardSizeContext)
  return <div ref={ref} className={cn('flex items-center', PAD[size].body, className)} {...props} />
})
